import { isDesktopShell } from "../../common/hooks/useIsDesktopView";
import { articleDetailQuery } from "../../features/article/hooks/useArticleData";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { z } from "zod";
import {
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
} from "@tabler/icons-react";
import { useChatScrollAnchor } from "../../domains/gpt/hooks/useChatScrollAnchor";
import { displayToast } from "../../components/app/AppToast";
import ContentMarkdown, { noCitations } from "../../components/ContentMarkdown";
import { ChatActions } from "../../components/gpt/chat/ChatActions";
import { ChatInput } from "../../components/ui/ChatInput";
import { useKeyboardManaged } from "../../hooks/use-keyboard-managed";
import { ChatLoader } from "../../components/ui/ChatLoader";
import { ChatMessageCard } from "../../components/ui/ChatMessageCard";
import { GPTConversationWrapper } from "../../components/gpt/layout";
import KeyboardAvoidingView from "../../components/layout/KeyboardAvoidingView";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useKeyboardHeight } from "../../hooks/use-keyboard-height";
import { cn } from "../../lib/utils";
import Loader from "../../components/Loader";
import StarsIcon from "../../assets/icons/stars.svg?react";

import LemonIcon from "../../assets/icons/cwyop-lemon.svg?react";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import {
  useGptChat,
  uiMessageText,
  questionOriginSchema,
  type QuestionOrigin,
} from "../../domains/gpt/hooks/useGptChat";
import { cleanAnswer, GPT_ANSWER_CLASS } from "../../domains/gpt/answer";
import { useFirebaseUser } from "../../domains/user/hooks/useFirebaseUser";
import { isJuisciShortUrlSession } from "../../domains/app/utils/juisciShortUrlSession";
import { gaEventArticleChatQuestion } from "../../tools/analytics/domains/article.analytics";
import { Trans, useTranslation } from "react-i18next";
import { iosGenerateHaptic } from "../../tools/ios";
import { HapticEffect } from "../../interfaces";
import ContentHeader from "../../components/app/headers/ContentHeader";
import {
  useConsumeLocationState,
  useLocationState,
} from "../../domains/app/hooks/useLocationState";
import { useApiQuery, useApiRevalidate, type Api } from "../../services/api";
import { useVisualAbstractBand } from "../../features/visual-abstract/useVisualAbstractBand";
import {
  chatGroundingPlaceholder,
  usePaperGrounding,
  type PaperGroundingKind,
} from "../../domains/gpt/hooks/usePaperGrounding";
import { VisualAbstractSuggestionCard } from "../../components/gpt/chat/VisualAbstractSuggestionCard";
import { VisualAbstractGeneratingNotice } from "../../components/gpt/chat/VisualAbstractGeneratingNotice";

type ArticleBody =
  Api["get"]["/webapp/public/articles/:slug"]["output"]["body"];

const locationStateSchema = z.object({
  initialQuestion: z.string().nullable().default(null),
  // How the handed-over question was chosen: typed in a composer, or tapped
  // among the paper's template questions on the desktop article page.
  initialQuestionOrigin: questionOriginSchema.default("typed"),
});

export default function ChatWithPaperPage({
  slug,
  roomId,
}: {
  slug: string;
  roomId: string | null;
}): React.ReactElement {
  useKeyboardManaged();
  const articleQuery = useApiQuery("/webapp/public/articles/:slug", {
    param: { slug },
    query: articleDetailQuery(roomId),
  });
  const article = articleQuery.state === "success" ? articleQuery.body : null;
  const { isAuthenticatedFirebase } = useFirebaseUser();
  const isAnonymousShortUrl =
    !isAuthenticatedFirebase && isJuisciShortUrlSession();

  if (article === null) return <Loader expanded />;

  return (
    <Landing
      key={article.id}
      slug={slug}
      roomId={roomId}
      article={article}
      isAnonymous={isAnonymousShortUrl}
    />
  );
}

/** The empty state's hint, when the paper carries neither questions nor TL;DR. */
function emptyStateHint(
  t: (key: string) => string,
  kind: PaperGroundingKind
): string {
  switch (kind) {
    case "full_text":
      return t("Ask a question about this paper");
    case "abstract":
      return t("Ask a question about this abstract");
    case "digest":
      return t("Ask a question about this Juisci summary");
    default:
      throw new Error(kind satisfies never);
  }
}

/**
 * The hero line. One key per kind rather than `t("Chat with") + t(noun)`: the
 * noun does not sit at the end of the sentence in every language, and the two
 * fragments pinned English word order into all eight locales. The accent colour
 * rides inside the key as `<c>`, so a translator moves it with the noun.
 */
function heroLine(kind: PaperGroundingKind): React.ReactElement {
  const components = { c: <span className="text-ds-primary" /> };
  switch (kind) {
    case "full_text":
      return <Trans i18nKey="Chat with <c>paper</c>" components={components} />;
    case "abstract":
      return (
        <Trans i18nKey="Chat with <c>abstract</c>" components={components} />
      );
    case "digest":
      return (
        <Trans
          i18nKey="Chat with <c>the Juisci summary</c>"
          components={components}
        />
      );
    default:
      throw new Error(kind satisfies never);
  }
}

/**
 * The line under the hero. It promises what the grounding can keep: source
 * backing only when the source's own body is what the agent reads, and a plain
 * warning otherwise. The `digest` wording says whose words they are — Juisci's
 * summary is not the publisher's abstract, and an HCP is entitled to know which
 * of the two an answer came from.
 */
function heroSubline(kind: PaperGroundingKind): React.ReactElement {
  // One key per sentence, per the copy rules: the emphasis rides inside the
  // key as `<b>`, so a translator moves the bold with the words it belongs to.
  // These used to be four concatenated fragments, which fixes English word
  // order into every locale.
  const components = { b: <b />, br: <br /> };
  switch (kind) {
    case "full_text":
      return (
        <Trans
          i18nKey="<b>Source-backed answers</b> with one-tap verification."
          components={components}
        />
      );
    case "abstract":
      return (
        <Trans
          i18nKey="This chat is based on <b>the abstract only,</b><br/>so answers will be <b>less detailed</b>."
          components={components}
        />
      );
    case "digest":
      return (
        <Trans
          i18nKey="This chat is based on <b>the Juisci summary only,</b><br/>so answers will be <b>less detailed</b>."
          components={components}
        />
      );
    default:
      throw new Error(kind satisfies never);
  }
}

function Landing({
  slug,
  roomId,
  article,
  isAnonymous,
}: {
  slug: string;
  roomId: string | null;
  article: ArticleBody;
  isAnonymous: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const revalidate = useApiRevalidate();
  const { chatWithPaperConversationLink } = usePageLinks(roomId ?? null);
  const articleId = article.id;
  // What the agent will be given, so the page can name it. `null` only for the
  // frame before the answer lands: this query is two indexed reads against the
  // article payload's own joins, and the surface that sent the reader here has
  // usually warmed it under the same key.
  const grounding = usePaperGrounding(slug);
  const groundingKind = grounding.status === "ready" ? grounding.kind : null;

  const { initialQuestion, initialQuestionOrigin } =
    useLocationState(locationStateSchema);
  const consumeLocationState = useConsumeLocationState();
  const [inputQuestion, setInputQuestion] = useState("");
  const { isKeyboardOpen } = useKeyboardHeight();
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  // Show the latest turn on open; never auto-follow the streamed answer.
  const { scrollRef } = useChatScrollAnchor({ initialToBottom: true });

  // Authenticated users may already have a persisted transcript for this
  // paper (they re-enter via the landing route every time). Count its prior
  // questions so the first turn asked here is tracked as a continuation rather
  // than a fresh conversation. Anonymous sessions never persist, so skip it.
  const historyQuery = useApiQuery(
    "/webapp/private/articles/:id/chat-with-paper/history",
    isAnonymous ? false : { param: { id: articleId } }
  );
  const persistedQuestionCount =
    historyQuery.state === "success"
      ? historyQuery.body.messages.filter((m) => m.role === "user").length
      : 0;

  const chat = useGptChat({
    transport: {
      kind: "paper",
      articleId,
      roomId: roomId ?? null,
      isAnonymous,
    },
    initialMessages: [],
    onTurnAccepted: null,
    // Authenticated turns persist server-side, so hand off to the conversation
    // route (which resumes from the DB). Anonymous sessions have no persisted
    // history to resume, so they stay here and keep the streamed answer visible.
    //
    // The stream ends once the turn is saved, and the history is refetched
    // before the hand-off: the conversation page seeds from it, and the cached
    // copy (read when this page opened) lacks the turn. Seeded from that copy,
    // the first answer showed with no sources until a reload.
    onTurnFinish: (question, answer) => {
      if (isAnonymous) return;
      void revalidate("/webapp/private/articles/:id/chat-with-paper/history", {
        param: { id: articleId },
      }).then(() => {
        history.replace(chatWithPaperConversationLink(slug, articleId), {
          question,
          answer,
        });
      });
    },
    // No broken-stream recovery here (see `useGptStreamRecovery`, wired on the
    // general JuisciGPT surfaces): the anonymous route persists nothing to come
    // back for, and the authenticated paper transcript exposes no per-turn
    // landmark to recognise a recovered turn by. A dropped connection keeps
    // showing the error + Retry.
    onFailure: () => {},
  });

  const isStreaming =
    chat.status === "submitted" || chat.status === "streaming";
  const lastMessage = chat.messages.at(-1);
  const streamedAnswer =
    lastMessage !== undefined && lastMessage.role === "assistant"
      ? uiMessageText(lastMessage)
      : "";
  const askedQuestion = chat.messages.find((m) => m.role === "user");
  const isAwaitingFirstToken =
    chat.status === "submitted" ||
    (chat.status === "streaming" && streamedAnswer === "");

  function submit(question: string, origin: QuestionOrigin): void {
    if (question === "" || isStreaming) return;
    const priorQuestionCount =
      persistedQuestionCount +
      chat.messages.filter((m) => m.role === "user").length;
    gaEventArticleChatQuestion({
      article,
      question,
      conversationHistory: priorQuestionCount === 0 ? "new" : "old",
      questionNumber: priorQuestionCount + 1,
      questionOrigin: origin,
      engine: "legacy",
    });
    void chat.sendMessage({ text: question }, { body: { origin } });
  }

  // Entry points that pre-type the question (e.g. the article page's docked
  // composer) hand it over via `initialQuestion` and expect it submitted on
  // arrival — mirror the JuisciGPT new-chat landing. The hand-off is consumed
  // before it is acted on: this entry stays current until the end-of-stream
  // redirect below, and a browser closed before that point restores it — state
  // included — on reopen. Left in place, the command re-submitted the question
  // as a real, persisted turn on every reopen (the duplicated visual-abstract
  // turns of 2026-08-27).
  useEffect(() => {
    if (initialQuestion !== null && initialQuestion.length > 0) {
      consumeLocationState();
      submit(initialQuestion, initialQuestionOrigin);
    }
  }, []);

  function handleCopyAnswer(question: string, text: string): void {
    const copyText =
      question +
      "\n\n" +
      text +
      `\n\n${t("Powered by JuisciGPT")} - https://app.juisci.com`;
    navigator.clipboard.writeText(copyText);
    displayToast(t("Copied"), "success");
  }

  function comingSoon(): void {
    displayToast(t("Coming soon"), "info");
  }

  // This page, not just the conversation, is where the FIRST answer streams:
  // the article page hands its question over here and only redirects to the
  // conversation route once the turn finishes. An offer wired to the
  // conversation alone is therefore absent for the whole first answer, which is
  // exactly the moment it is meant to be seen.
  const visualAbstractBand = useVisualAbstractBand({
    articleId,
    articleTitle: article.title,
    roomId,
  });

  const templateQuestions = article.templateQuestions;
  const tldr = article.tldr ?? "";
  const hasConversation = askedQuestion !== undefined;

  return (
    <ScrollableLayout
      background="var(--color-ds-cream)"
      containerStyle={{ background: "var(--color-ds-cream)" }}
      headerClassName="bg-white/90 backdrop-blur-md"
      header={<ContentHeader transparent />}
      scrollRef={scrollRef}
    >
      <div className="page-wrapper">
        <GPTConversationWrapper className="bg-ds-cream pt-4 pb-[calc(120px+var(--safe-area-bottom))]">
          {hasConversation ? (
            <>
              <ChatMessageCard
                variant={isDesktopShell() ? "desktop" : "default"}
                className="mx-5"
              >
                {uiMessageText(askedQuestion)}
              </ChatMessageCard>
              {isAwaitingFirstToken ? (
                <ChatLoader variant="cwyop" />
              ) : chat.status === "error" ? (
                <p className="my-0 mx-5 text-[14px] font-normal leading-[26px] text-secondary-raspberry-1">
                  {t("❌ Sorry an error has occurred. Please retry.")}
                </p>
              ) : (
                <div className="flex flex-col gap-[12px] px-5">
                  <ContentMarkdown
                    className={GPT_ANSWER_CLASS}
                    math={true}
                    linkVariant="tag"
                    streaming={isStreaming}
                    citation={noCitations}
                  >
                    {cleanAnswer(streamedAnswer, t("See more"))}
                  </ContentMarkdown>
                  {!isStreaming && (
                    <ChatActions
                      onCopy={() => {
                        handleCopyAnswer(
                          uiMessageText(askedQuestion),
                          cleanAnswer(streamedAnswer, t("See more"))
                        );
                      }}
                      onShare={comingSoon}
                      showShare={false}
                      onPlay={comingSoon}
                      onThumbUp={comingSoon}
                      onThumbDown={comingSoon}
                    />
                  )}
                </div>
              )}
            </>
          ) : (
            /* Landing view: hero + AI summary + template questions */
            <div className="flex flex-1 flex-col items-center px-5">
              {/* Hero — held back until the grounding is known, because every
                  word of it names the grounding. */}
              {groundingKind !== null && (
                <div className="flex flex-col items-center gap-3 mt-4">
                  <div className="flex items-center gap-2">
                    <LemonIcon className="h-[40px] w-auto shrink-0" />
                    <p className="font-dm-sans font-semibold text-[24px] leading-[1.1] tracking-[-0.6px] text-center m-0 text-ds-mint-800">
                      {heroLine(groundingKind)}
                    </p>
                  </div>
                  <p className="font-dm-sans text-[12px] leading-[18px] text-ds-mint-800 text-center m-0">
                    {heroSubline(groundingKind)}
                  </p>
                </div>
              )}

              {/* AI Summary */}
              {tldr !== "" && (
                <div className="bg-ds-mint-100 rounded-[19px] px-[21px] py-[12px] mt-[16px] w-full max-w-cwyop-column">
                  <div className="flex flex-col gap-[7px]">
                    <div className="flex items-center gap-[3px]">
                      <StarsIcon className="w-[10px] h-[11px]" />
                      <span className="font-['DM_Sans',sans-serif] font-medium text-[12px] leading-[1.4] text-[#074438]">
                        {t("AI Summary")}
                      </span>
                    </div>
                    <p
                      className={`font-['DM_Sans',sans-serif] font-normal text-[13px]/[1.3]  tracking-[-0.12px] text-ds-mint-800 m-0 ${
                        summaryExpanded ? "" : "line-clamp-3 overflow-hidden"
                      }`}
                    >
                      {tldr}
                    </p>
                    <button
                      className="flex items-center gap-[2px] bg-transparent border-none cursor-pointer p-0 m-0"
                      onClick={() => {
                        setSummaryExpanded((prev) => !prev);
                      }}
                    >
                      <span className="font-['DM_Sans',sans-serif] font-bold text-[12px] leading-[18px] text-[#074438]">
                        {summaryExpanded ? t("Show less") : t("Read more")}
                      </span>
                      {summaryExpanded ? (
                        <IconChevronUp
                          size={14}
                          color="#074438"
                          className="stroke-[2.5]"
                        />
                      ) : (
                        <IconChevronDown
                          size={14}
                          color="#074438"
                          className="stroke-[2.5]"
                        />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Separator */}
              {templateQuestions.length > 0 && tldr !== "" && (
                <div className="w-full max-w-cwyop-column mt-[16px]">
                  <div className="h-px bg-ds-black opacity-10" />
                </div>
              )}

              {/* Template questions */}
              {templateQuestions.length > 0 && (
                <div className="flex flex-col items-center gap-[14px] mt-[20px] w-full max-w-cwyop-column">
                  {(showAllQuestions
                    ? templateQuestions
                    : templateQuestions.slice(0, 2)
                  ).map((question, index) => (
                    <button
                      key={index}
                      className="bg-white w-full p-[11px_21px]  rounded-3xl border border-solid border-secondary-light-grey cursor-pointer flex items-center gap-[16px]"
                      onClick={() => {
                        submit(question, "cwop_template");
                      }}
                    >
                      <p className="font-dm-sans font-medium text-footnote/normal  tracking-[-0.15px] text-ds-mint-800 text-left m-0 flex-1">
                        {question}
                      </p>
                      <IconChevronRight
                        size={16}
                        color="#135345"
                        className="shrink-0 stroke-2"
                      />
                    </button>
                  ))}
                  {!showAllQuestions && templateQuestions.length > 2 && (
                    <button
                      className="flex items-center gap-[2px] bg-transparent border-none cursor-pointer p-0 m-0"
                      onClick={() => {
                        setShowAllQuestions(true);
                      }}
                    >
                      <span className="font-dm-sans font-medium text-[13px] leading-[19.5px] text-ds-mint-800">
                        {`${String(templateQuestions.length - 2)} ${t("more suggestions")}`}
                      </span>
                      <IconChevronDown
                        size={16}
                        color="#135345"
                        className="stroke-2"
                      />
                    </button>
                  )}
                </div>
              )}

              {/* Fallback when no questions and no tldr */}
              {templateQuestions.length === 0 &&
                tldr === "" &&
                groundingKind !== null && (
                  <div className="flex flex-1 items-center justify-center text-center text-gray-400 mt-8">
                    {emptyStateHint(t, groundingKind)}
                  </div>
                )}
            </div>
          )}

          {!isAwaitingFirstToken && (
            <p className="text-center text-[10px] font-light text-[#212121] m-0">
              {t("AI can make mistakes. Consider checking Source information.")}
            </p>
          )}
        </GPTConversationWrapper>
      </div>

      <KeyboardAvoidingView
        behavior="position"
        transitionDuration={0.22}
        style={{ position: "fixed", bottom: 0, width: "100%" }}
      >
        {/* Keyboard open: home indicator is covered by the keyboard, so collapse
            the safe-area bottom reserve to a tight visual gap. */}
        <div
          className={cn(
            "w-full px-5 pt-[10px] bg-ds-cream",
            isKeyboardOpen
              ? "pb-2"
              : "pb-[calc(16px+var(--safe-area-bottom)/2)]"
          )}
        >
          {/* A STATUS stacks above the composer; a PROPOSAL takes its place —
              the same split the conversation's band makes, so the offer reads
              as the turn's pending question rather than one more thing on the
              screen. It costs the Stop button while a turn is in flight:
              dismissing the card brings the composer, and the stop, straight
              back. */}
          <AnimatePresence>
            {visualAbstractBand.isGenerating && (
              <motion.div
                key="generating"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 16 }}
                transition={{ duration: 0.32, ease: "easeOut" }}
              >
                <VisualAbstractGeneratingNotice />
              </motion.div>
            )}
          </AnimatePresence>
          {/* `mode="wait"` so the outgoing widget clears before the incoming one
              slides up: the swap reads as a replacement rather than a
              cross-fade. Same motion as the conversation's band, because it is
              the same gesture on the same slot.

              The entrance is refused PER CHILD rather than by the presence's
              `initial={false}`, which suppresses it for every child present on
              the first render — and the offer usually IS, because the article
              page has already warmed this query and react-query answers it from
              cache before the first paint. The composer still arrives without
              motion on a cold load; the card always slides up. */}
          <AnimatePresence mode="wait">
            <motion.div
              key={visualAbstractBand.suggestion === null ? "input" : "offer"}
              initial={
                visualAbstractBand.suggestion === null
                  ? false
                  : { y: 24, opacity: 0 }
              }
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 24, opacity: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              {visualAbstractBand.suggestion !== null ? (
                <VisualAbstractSuggestionCard
                  articleId={visualAbstractBand.suggestion.articleId}
                  onShown={visualAbstractBand.suggestion.onShown}
                  onGenerate={visualAbstractBand.suggestion.onGenerate}
                  onDismiss={visualAbstractBand.suggestion.onDismiss}
                />
              ) : (
                <ChatInput
                  className="w-full"
                  placeholder={
                    groundingKind === null
                      ? t("Ask anything")
                      : chatGroundingPlaceholder(t, groundingKind)
                  }
                  value={inputQuestion}
                  onChange={(e) => {
                    setInputQuestion(e.target.value);
                  }}
                  mode={null}
                  leading={null}
                  leadingPanel={null}
                  banner={null}
                  attach={null}
                  dictation={null}
                  stream={
                    isStreaming
                      ? { kind: "streaming", onStop: () => chat.stop() }
                      : null
                  }
                  onSend={() => {
                    iosGenerateHaptic(HapticEffect.SUCCESS);
                    submit(inputQuestion, "typed");
                    setInputQuestion("");
                  }}
                  sendDisabled={false}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </KeyboardAvoidingView>
    </ScrollableLayout>
  );
}
